import { Observable, of, throwError } from 'rxjs';
import { mapTo, mergeMap } from 'rxjs/operators';
import {
  ApiEndpointConfiguration,
  EmptyResponse,
  Guid,
  IMobizHttpService,
  MobizServerApi,
  PagedResults,
  ParameterValueType,
} from '../../core';
import { Inject, Injectable, Optional } from '../../injection';

import { Notification } from '../data/notification.do';
import {
  INotificationsQueryParameters,
  INotificationsService,
  INotificationSubscriptionOptions,
} from '../interfaces/notifications.interface';
import type { DeviceTokenFactory } from '../other/device-token-factory';
import { DEVICE_TOKEN_FACTORY } from '../other/device-token-factory';
import { PUSH_PLATFORM, PushPlatform } from '../other/push-platform.enum';

@Injectable()
export class NotificationsService implements INotificationsService {
  constructor(
    @Inject(IMobizHttpService) private readonly httpService: IMobizHttpService,
    @Inject(DEVICE_TOKEN_FACTORY) @Optional() private readonly deviceTokenFactory: DeviceTokenFactory | null,
    @Inject(PUSH_PLATFORM) @Optional() private readonly pushPlatform: PushPlatform | null,
  ) {
  }

  public getNotifications(queryParameters: INotificationsQueryParameters): Observable<PagedResults<Notification>> {
    const actualParameters: Record<string, ParameterValueType> = {};
    Object.keys(queryParameters).forEach((name: keyof INotificationsQueryParameters): void => {
      const parameterValue: ParameterValueType | Date = queryParameters[name];
      actualParameters[name] = parameterValue instanceof Date ? parameterValue.toISOString() : parameterValue;
    });
    const urlParameters: Record<string, ParameterValueType> = {};
    if (actualParameters.organizationId) {
      urlParameters.organizationId = actualParameters.organizationId;
      delete actualParameters.organizationId;
    }
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.Notifications,
      queryParameters: actualParameters,
      urlParameters: urlParameters,
    };
    const list: PagedResults<Notification> = new PagedResults(Notification);
    return this.httpService.get(list, config);
  }

  public subscribe(organizationIds: Guid[]): Observable<void> {
    if (this.deviceTokenFactory === null || this.pushPlatform === null) {
      return this.getPushNotificationsValidationErrors();
    }
    const pushPlatform = this.pushPlatform;
    return this.deviceTokenFactory().pipe(
      mergeMap((deviceToken: string | undefined): Observable<unknown> => {
        if (deviceToken === undefined) {
          return of(undefined);
        }
        const subscription: INotificationSubscriptionOptions = {
          deviceToken: deviceToken,
          organizationIds: organizationIds,
          pushPlatform: pushPlatform,
        };
        const config: ApiEndpointConfiguration = {
          serverApi: MobizServerApi.NotificationsSubscribe,
          body: JSON.stringify(subscription),
          disableErrorHandling: true,
        };
        return this.httpService.post(new EmptyResponse(), config);
      }),
      mapTo(undefined),
    );
  }

  public unsubscribe(organizationIds: Guid[]): Observable<void> {
    if (this.deviceTokenFactory === null || this.pushPlatform === null) {
      return this.getPushNotificationsValidationErrors();
    }
    const pushPlatform = this.pushPlatform;
    return this.deviceTokenFactory().pipe(
      mergeMap((deviceToken: string | undefined): Observable<unknown> => {
        if (deviceToken === undefined) {
          return of(undefined);
        }
        const subscription: INotificationSubscriptionOptions = {
          deviceToken: deviceToken,
          organizationIds: organizationIds,
          pushPlatform: pushPlatform,
        };
        const config: ApiEndpointConfiguration = {
          serverApi: MobizServerApi.NotificationsUnsubscribe,
          body: JSON.stringify(subscription),
          disableErrorHandling: true,
        };
        return this.httpService.post(new EmptyResponse(), config);
      }),
      mapTo(undefined),
    );
  }

  private getPushNotificationsValidationErrors(): Observable<never> {
    const errors: string[] = [];
    if (this.deviceTokenFactory === null) {
      errors.push('DEVICE_TOKEN_FACTORY is required');
    }
    if (this.pushPlatform === null) {
      errors.push('PUSH_PLATFORM is required');
    }
    const message = `Can not subscribe to push notifications! ${errors.join('')}`;
    return throwError(new Error(message));
  }
}
